// src/serviceWorkerRegistration.ts

// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// Helper function to check if we should completely disable service workers
function shouldDisableServiceWorker(): boolean {
  // Check for both specific flags in localStorage
  const hasFirestoreError = localStorage.getItem("firestore-error") === "true";
  const serviceWorkerDisabled =
    localStorage.getItem("pwa-disable-sw") === "true";

  // Also check URL params - useful for debugging/testing
  const urlParams = new URLSearchParams(window.location.search);
  const disableInUrl = urlParams.get("disable-sw") === "true";

  // Check for recent dynamic import errors
  const recentErrors = localStorage.getItem("dynamic-import-errors");
  const hasRecentImportErrors = recentErrors
    ? Date.now() - parseInt(recentErrors) < 300000
    : false; // 5 minutes

  return (
    hasFirestoreError ||
    serviceWorkerDisabled ||
    disableInUrl ||
    hasRecentImportErrors
  );
}

export function register(config?: Config) {
  // Immediately unregister service worker if it's disabled
  if (shouldDisableServiceWorker()) {
    unregister();
    return;
  }

  // Never use the production Workbox SW during Vite dev: it precaches hashed
  // /assets/* from the last build and breaks module loading (wrong MIME, SW errors).
  if (import.meta.env.DEV) {
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          void registration.unregister();
        }
      });
    }
    return;
  }

  // Basic feature detection
  if (
    !window.isSecureContext ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }

  // The URL constructor is available in all browsers that support SW.
  const publicUrl = new URL(
    import.meta.env.BASE_URL || "",
    window.location.href
  );
  if (publicUrl.origin !== window.location.origin) {
    // Our service worker won't work if PUBLIC_URL is on a different origin
    // from what our page is served on. This might happen if a CDN is used to
    // serve assets; see https://github.com/facebook/create-react-app/issues/2374
    return;
  }

  window.addEventListener("load", () => {
    const swUrl = `${window.location.origin}/sw.js`;

    if (isLocalhost) {
      // This is running on localhost. Let's check if a service worker still exists or not.
      checkValidServiceWorker(swUrl, config);
    } else {
      // Is not localhost. Just register service worker
      registerValidSW(swUrl, config);
    }
  });

  // Add event listener for message events to handle navigation requests
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "SKIP_WAITING") {
      // We can't call skipWaiting directly here, as it's a method on the ServiceWorker
      // Instead, we need to relay the message to the active service worker
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "SKIP_WAITING",
        });
      }
    }
  });

  // Add global error handler to detect Firestore issues with service worker
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason && event.reason.message) {
      const errorMsg = event.reason.message.toString();
      // Check for any Firestore or Firebase related errors that might be related to service worker
      if (
        (errorMsg.includes("firestore") ||
          errorMsg.includes("firebase") ||
          errorMsg.includes("ServiceWorker") ||
          errorMsg.includes("cache") ||
          errorMsg.includes("channel") ||
          errorMsg.includes("Listen")) &&
        (errorMsg.includes("ServiceWorker") ||
          errorMsg.includes("intercepted") ||
          errorMsg.includes("failed"))
      ) {
        console.error(
          "Detected potential Firebase error with service worker:",
          errorMsg
        );
        localStorage.setItem("firestore-error", "true");
        localStorage.setItem("pwa-disable-sw", "true");
        localStorage.setItem("sw-disabled-timestamp", Date.now().toString());

        // Immediately unregister all service worker registrations
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            for (let registration of registrations) {
              registration.unregister();
            }
            // Reload the page only after successful unregistration
            window.location.reload();
          });
        } else {
          // If we can't access the service worker API, just reload
          window.location.reload();
        }
      }
    }
  });
}

function registerValidSW(swUrl: string, config?: Config) {
  try {
    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker == null) {
            return;
          }
          installingWorker.onstatechange = () => {
            if (installingWorker.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // At this point, the updated precached content has been fetched,
                // but the previous service worker will still serve the older
                // content until all client tabs are closed.

                // Execute callback
                if (config && config.onUpdate) {
                  config.onUpdate(registration);
                }
              } else {
                // At this point, everything has been precached.
                // Content is cached for offline use.

                // Execute callback
                if (config && config.onSuccess) {
                  config.onSuccess(registration);
                }
              }
            }
          };
        };
      })
      .catch((error) => {
        console.error("Error during service worker registration:", error);
      });
  } catch (error) {
    console.error("Error attempting to register service worker:", error);
  }
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      // No internet connection - app is running in offline mode
    });
}

// More aggressive unregister that handles all registrations and returns a promise
export function unregisterAll(): Promise<boolean> {
  if ("serviceWorker" in navigator) {
    return navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        const promises = registrations.map((registration) =>
          registration.unregister()
        );
        return Promise.all(promises).then(() => true);
      })
      .catch((error) => {
        console.error("Error unregistering service workers:", error);
        return false;
      });
  }
  return Promise.resolve(false);
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
